import React from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'

/** Shared Tailwind classes for auth email/password (and similar) inputs */
export const AUTH_INPUT_CLASS =
  'h-11 border-zinc-500 bg-zinc-950 text-base text-zinc-50 placeholder:text-zinc-500 focus-visible:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-500/40'

/** Multi-line fields: do not use `h-11` from inputs (it breaks vertical alignment / placeholder position). */
export const AUTH_TEXTAREA_CLASS =
  'min-h-[100px] w-full resize-y rounded-md border border-zinc-500 bg-zinc-950 px-3 py-2 text-base leading-relaxed text-zinc-50 placeholder:text-zinc-500 focus-visible:border-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40'

const shellClassDefault =
  'flex w-full max-w-md shrink-0 flex-col gap-6 self-center px-1 sm:px-0'
const shellClassWide =
  'flex w-full max-w-md shrink-0 flex-col gap-6 self-center px-1 sm:max-w-lg sm:px-0'

export type AuthPageShellProps = {
  /** Use wider max width on `sm+` (e.g. signup with more fields) */
  wide?: boolean
  icon: React.ReactNode
  title: string
  description: string
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void | Promise<void>
  /** Form fields only; error alert is rendered after children */
  children: React.ReactNode
  error?: string | null
  isLoading: boolean
  submitIdleLabel: string
  submitLoadingLabel: string
  footer: React.ReactNode
}

export function AuthPageShell({
  wide = false,
  icon,
  title,
  description,
  onSubmit,
  children,
  error,
  isLoading,
  submitIdleLabel,
  submitLoadingLabel,
  footer,
}: AuthPageShellProps): JSX.Element {
  return (
    <div className={wide ? shellClassWide : shellClassDefault}>
      <Card className="border border-zinc-600 bg-zinc-900 text-zinc-50 shadow-2xl">
        <CardHeader className="space-y-3 px-6 pb-2 pt-6 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-400/40">
            {icon}
          </div>
          <CardTitle className="text-2xl font-semibold tracking-tight text-zinc-50">{title}</CardTitle>
          <CardDescription className="text-base leading-relaxed text-zinc-300">{description}</CardDescription>
        </CardHeader>
        <form onSubmit={onSubmit}>
          <CardContent className="space-y-5 px-6 pb-2">
            {children}
            {error ? (
              <div
                role="alert"
                className="rounded-md border border-red-400/60 bg-red-950/80 px-4 py-3 text-sm leading-relaxed text-red-50"
              >
                {error}
              </div>
            ) : null}
          </CardContent>
          <CardFooter className="flex flex-col gap-4 px-6 pb-6 pt-4">
            <Button
              type="submit"
              className="h-11 w-full bg-emerald-600 text-base font-semibold text-white shadow hover:bg-emerald-500"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="size-4 animate-spin" aria-hidden />
                  {submitLoadingLabel}
                </>
              ) : (
                submitIdleLabel
              )}
            </Button>
            {footer}
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
